import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { VaccinationStatusType } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { VaccinationStatusState } from '../../entities';
import { VaccinationsStatusAction } from '../actions';

export const vaccinationStatusAdapter: EntityAdapter<VaccinationStatusType> =
    createEntityAdapter<VaccinationStatusType>();

export const initialState: VaccinationStatusState = vaccinationStatusAdapter.getInitialState({
    error: null,
    isLoading: false,
});

export const vaccinationStatusReducer = createReducer(
    initialState,
    on(VaccinationsStatusAction.loadVaccinations, state => ({ ...state, isLoading: true })),
    on(VaccinationsStatusAction.loadVaccinationsSuccess, (state, { data }) =>
        // Use upsertMany instead of setAll so optimistic local entries (added/updated between
        // dispatching `loadVaccinations` and the response arriving, or by a WS push that landed
        // before the GET completed) are not wiped out. Deletions propagate via the dedicated
        // `addVaccinationsChanges` action (WS push).
        vaccinationStatusAdapter.upsertMany(data, {
            ...state,
            error: null,
            isLoading: false,
        }),
    ),
    on(VaccinationsStatusAction.loadVaccinationsFailure, (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(VaccinationsStatusAction.loadVaccinationsCancelled, state => ({
        ...state,
        isLoading: false,
        error: null,
    })),
    on(VaccinationsStatusAction.updateVaccinations, (state, action) =>
        vaccinationStatusAdapter.updateMany(action.update, state),
    ),
    on(VaccinationsStatusAction.upsertVaccinations, (state, action) =>
        vaccinationStatusAdapter.upsertMany(action.data, state),
    ),
    /**
     * Defensive reset: belt-and-suspenders against any future regression of
     * `loadVaccinations$` silently completing via
     * `takeUntil(isEmptyEncounterId)`. Same bug shape as the MD-9750 stuck-
     * spinner family already fixed for daily-goals, procedure-prescriptions
     * and ventilation.
     */
    on(VaccinationsStatusAction.clearVaccinations, EncountersActions.clearEncounter, state =>
        vaccinationStatusAdapter.removeAll({
            ...state,
            isLoading: false,
            error: null,
        }),
    ),
    on(VaccinationsStatusAction.addVaccinationsChanges, (state, { toRemoveIds, toUpdateEntities }) => {
        const newState = vaccinationStatusAdapter.removeMany(toRemoveIds, state);

        return vaccinationStatusAdapter.upsertMany(toUpdateEntities, newState);
    }),
);
